// Text equivalents for charts and the aria-live summary. Every chart pairs with
// one of these so nothing depends on seeing the pixels.
import type { RaceConfig } from '../types/config.ts';
import type { Margin } from './reconcile.ts';
import type { MarginPoint } from '../types/snapshots.ts';
import { candidateName } from './candidates.ts';
import { fmtTimeOfDay } from './time.ts';

/** One-sentence summary of the current headline, for the aria-live region and
 *  the chart text equivalent. Flat and factual — no momentum language. */
export function marginSentence(race: RaceConfig, margin: Margin | null): string {
  if (!margin || !margin.leaderId) return 'No leader yet; awaiting reported votes.';
  const leader = candidateName(race, margin.leaderId);
  const runner = candidateName(race, margin.runnerUpId);
  const rep =
    margin.reportingPercent != null
      ? `${margin.reportingPercent.toFixed(1)}% reporting`
      : 'reporting percentage unknown';
  if (margin.marginPct == null) return `${leader} leads; margin unknown; ${rep}.`;
  return `${leader} leads ${runner} by ${margin.marginPct.toFixed(1)} points; ${rep}.`;
}

/** Rows describing the margin trend, for a visually-hidden data table. */
export function marginSeriesRows(
  race: RaceConfig,
  series: MarginPoint[],
): { time: string; leader: string; margin: string; reporting: string }[] {
  return series.map((p) => ({
    time: fmtTimeOfDay(p.t),
    leader: p.leaderId ? candidateName(race, p.leaderId) : '—',
    margin: p.marginPct != null ? `${p.marginPct.toFixed(1)} pts` : '—',
    reporting: p.reportingPercent != null ? `${p.reportingPercent.toFixed(0)}%` : '—',
  }));
}
